<script lang="ts">
	/**
	 * `/Catalog` — authoring families, products, prices and coupons (Design §13, §14).
	 *
	 * Both catalog guard rails are enforced in `CatalogService`, not here. This page **mirrors**
	 * them so an operator is not surprised by a refusal: `entitlementKey` renders write-once past
	 * publication, and price editing offers "Add currency" on a live price and "New price" for
	 * everything else. A UI that invented its own rules would drift from the service and turn
	 * every refusal into a bug report.
	 */

	import { onMount } from 'svelte';
	import { Button, Modal, Toggle } from 'flowbite-svelte';
	import { EmptyState, ModalHeader, PageHeader } from '../../components';
	import { isArchivedFamily, isArchivedProduct } from '../../Utilities';
	import FamiliesPanel from './catalog/FamiliesPanel.svelte';
	import ProductsPanel from './catalog/ProductsPanel.svelte';
	import PricesPanel from './catalog/PricesPanel.svelte';
	import CouponsPanel from './catalog/CouponsPanel.svelte';
	import ClickwrapsPanel from './catalog/ClickwrapsPanel.svelte';
	import ProductOptionsPanel from './catalog/ProductOptionsPanel.svelte';
	import {
		getCatalogSyncStatus,
		listAdminFamilies,
		listAdminProducts,
		listCoupons,
		listPaymentProviders,
		listApplications,
		listClickwraps,
		listProductOptions,
		listSellers,
		syncCatalog
	} from '../../AppNetwork';
	import { toastError, toastSuccess, toastWarning } from '../../stores/toast';
	import { activeCurrency, activeCurrencyCode, availableCurrencies } from '../../stores/storefront';
	import type {
		CatalogSyncResult,
		CatalogSyncStatus,
		CatalogSyncStatusItem,
		Coupon,
		PaymentProviderConfig,
		Product,
		ProductFamily,
		AdminClickwrap,
		AdminProductOption,
		Seller,
		StoreApplication
	} from '../../AppTypes';

	let families: ProductFamily[] = [];
	/** §4.6. Only used to populate the product form's "Sold by" selector. */
	let sellers: Seller[] = [];
	/** §4.7 — the terms a buyer must tick, authored beside the catalogue they belong to. */
	let clickwraps: AdminClickwrap[] = [];
	let productOptions: AdminProductOption[] = [];
	/**
	 * §4.3 — the registered applications, so a product can name who reads it.
	 *
	 * Same standing as sellers and terms: one selector on the product form, and a store with no
	 * integrations is an ordinary store — so a failure loading them must not stop an admin
	 * authoring the catalogue.
	 */
	let applications: StoreApplication[] = [];
	let products: Product[] = [];
	let coupons: Coupon[] = [];
	let loading: boolean = true;
	let loadError: string = '';
	let selectedProductUUID: string = '';

	/**
	 * Whether archived lines and products are listed (§4.1).
	 *
	 * One switch for both panels, because "archived" is one idea and two independent toggles would
	 * let the screen show an archived product under a line that is not on it. Off by default: an
	 * archived thing is one the store has finished selling, and a catalogue that lists every plan
	 * it has ever retired buries the ones it sells now.
	 *
	 * Deliberately **not** a `storable`. Hiding is the default the store should open on, and a
	 * sticky "show archived" from three weeks ago is a catalogue an admin reads as the live one.
	 * The count beside the switch is what stops the hiding from being silent.
	 */
	let showArchived: boolean = false;

	let providers: PaymentProviderConfig[] = [];
	let syncing: boolean = false;
	let syncResult: CatalogSyncResult | null = null;

	/**
	 * Per-object state at the provider, read on every page load rather than only after a sync.
	 *
	 * `syncResult` is the report of the run that just happened and is gone on the next reload;
	 * this is the standing answer to "is the provider holding what we think it is". Without it a
	 * push that failed last Tuesday is invisible until somebody syncs again, and the only symptom
	 * in between is a Buy button that 500s.
	 */
	let syncStatus: CatalogSyncStatus | null = null;

	$: productSyncStatus = new Map<string, CatalogSyncStatusItem>(
		(syncStatus?.items ?? [])
			.filter(item => item.objectType === 'product')
			.map(item => [item.identifier, item])
	);

	/**
	 * Sync targets one provider explicitly. During a §5.3 migration both are active, and pushing
	 * to "the default" would silently leave the new provider with no catalog.
	 */
	$: syncTarget = providers.find(provider => provider.isDefault) ?? providers[0] ?? null;

	async function fnSync(): Promise<void> {
		if (syncTarget === null) {
			return;
		}
		syncing = true;
		syncResult = null;
		try {
			const result = await syncCatalog(syncTarget.uuid);
			syncResult = result;
			syncStatus = await getCatalogSyncStatus().catch(() => syncStatus);
			if (result.failed > 0) {
				// Not a success toast: a partial failure means the storefront cannot sell those
				// objects on that provider, and "sync ran" would read as if it could.
				toastWarning(
					`Synced ${result.synced} of ${result.total} to ${result.providerName}`,
					`${result.failed} failed. See the results below`
				);
			} else {
				toastSuccess(`Synced ${result.synced} objects to ${result.providerName}`);
			}
		} catch (error) {
			toastError('Catalogue sync failed', (error as Error).message);
		} finally {
			syncing = false;
		}
	}

	/**
	 * How many rows the switch is holding back, counted across both panels.
	 *
	 * A product inside an archived line is counted once, not twice: the line is what is hidden and
	 * the product goes with it, so counting both would tell an admin there are five archived things
	 * when there are two and three that came along.
	 */
	$: archivedFamilySlugs = new Set(
		families.filter(isArchivedFamily).map(family => family.slug)
	);
	$: archivedCount =
		archivedFamilySlugs.size +
		products.filter(
			product =>
				isArchivedProduct(product) &&
				!(product.familySlug !== null && archivedFamilySlugs.has(product.familySlug))
		).length;

	$: selectedProduct = products.find(product => product.uuid === selectedProductUUID) ?? null;

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			const currency = $activeCurrencyCode || undefined;
			const [familyResult, productResult, couponResult] = await Promise.all([
				listAdminFamilies({ currency }),
				listAdminProducts({ currency }),
				listCoupons()
			]);
			families = familyResult.items;
			products = productResult.items;
			coupons = couponResult.items;

			// Sellers are for one selector on the product form, so a store that has configured none
			// — or a failure loading them — must not stop an admin authoring the catalogue.
			try {
				sellers = (await listSellers()).items;
			} catch {
				sellers = [];
			}

			// Same reasoning as sellers: a store with no terms is the common case, and a failure
			// loading them must not stop an admin authoring the catalogue.
			try {
				clickwraps = (await listClickwraps()).items;
			} catch {
				clickwraps = [];
			}

			// §4.8, and the same reasoning as the terms: a store offering none is the common case.
			try {
				productOptions = (await listProductOptions()).items;
			} catch {
				productOptions = [];
			}

			// §4.3, and the same reasoning again.
			try {
				applications = (await listApplications()).items;
			} catch {
				applications = [];
			}

			// A sync needs somewhere to push to; failing to load providers must not break authoring.
			try {
				providers = (await listPaymentProviders()).items;
			} catch {
				providers = [];
			}
			// Same rule for the sync state: it is context for authoring, never a prerequisite of it.
			try {
				syncStatus = await getCatalogSyncStatus();
			} catch {
				syncStatus = null;
			}
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	onMount(fnLoad);
</script>

<PageHeader title="Catalogue">
	<svelte:fragment slot="description">
		Families, products, prices and coupons. The store stays closed until one active product has an
		active price.
	</svelte:fragment>
	<svelte:fragment slot="help">
		<p>
			<strong>The entitlement key</strong> is the only string consuming applications hard-code. It is
			frozen the first time a product is activated: every entitlement already issued carries it, so
			changing it would strand existing holders on the old key while new purchasers got the new one.
		</p>
		<p class="mt-2">
			<strong>Prices are immutable per operation.</strong> Adding a currency to a live price is fine, because
			it cannot reprice anyone. Changing an existing amount, interval or tax behaviour needs a new
			price; existing subscriptions keep what they were sold at.
		</p>
		<p class="mt-2">
			<strong>Syncing</strong> pushes the active catalogue into one payment provider. Authoring a
			product does not push it on its own, so the catalogue and the provider drift until this runs.
		</p>
	</svelte:fragment>
</PageHeader>

<div class="widget mb-4">
	<div class="flex flex-wrap items-center justify-between gap-3">
		<div>
			<div class="card-label">Provider catalogue</div>
			<p class="text-default text-sm">
				{#if syncTarget === null}
					No payment provider is configured yet, so there is nowhere to push the catalogue.
				{:else}
					Push every active product, price and coupon into
					<strong>{syncTarget.name}</strong>{#if syncTarget.mode === 'test'} (test mode){/if}.
				{/if}
			</p>
		</div>
		<Button size="sm" disabled={syncTarget === null || syncing} on:click={fnSync}>
			{syncing ? 'Syncing…' : 'Sync to provider'}
		</Button>
	</div>

	<!--
		The standing answer, as opposed to `syncResult`'s report of the run that just happened.
		Stated in words rather than colour alone, and listing what is wrong rather than only
		counting it — "3 out of date" with no names is a number nobody can act on.
	-->
	{#if syncStatus !== null && syncStatus.providerUUID !== null}
		<div class="mt-3 border-t border-widget-color pt-3" data-testid="catalog-sync-status">
			{#if syncStatus.outOfDate === 0}
				<p class="text-sm text-default">
					Every active product, price and coupon is synced to {syncStatus.providerName}.
				</p>
			{:else}
				<p class="text-sm font-medium text-amber-700 dark:text-amber-400">
					{syncStatus.outOfDate} of {syncStatus.items.length} objects are not synced to
					{syncStatus.providerName}. The storefront cannot sell what the provider does not have.
				</p>
				<ul class="mt-2 space-y-1">
					{#each syncStatus.items.filter(item => item.syncStatus !== 'synced') as item (item.objectType + item.identifier)}
						<li class="text-sm">
							<span class="font-medium">{item.objectType}</span>
							<span class="text-default">{item.name}</span>:
							<span class="text-amber-700 dark:text-amber-400"
								>{item.syncStatus === 'never_synced' ? 'never synced' : item.syncStatus}</span
							>{#if item.syncError}: <span class="text-red-600 dark:text-red-400">{item.syncError}</span>{/if}
						</li>
					{/each}
				</ul>
			{/if}
		</div>
	{/if}

	{#if syncResult !== null}
		<!-- Per object, never a single pass/fail: six products pushed and the seventh rejected must
		     not read as "sync ran" while one product quietly cannot be sold. -->
		<div class="mt-3 border-t border-widget-color pt-3">
			<p class="text-sm">
				{syncResult.synced} of {syncResult.total} synced to {syncResult.providerName}{#if syncResult.failed > 0}, <span class="font-semibold text-red-600 dark:text-red-400">{syncResult.failed} failed</span>{/if}.
			</p>
			{#if syncResult.failed > 0}
				<ul class="mt-2 space-y-1">
					{#each syncResult.results.filter(result => result.status === 'failed') as result (result.objectType + result.identifier)}
						<li class="text-sm">
							<span class="font-medium">{result.objectType}</span>
							<span class="text-default">{result.name}</span>:
							<span class="text-red-600 dark:text-red-400">{result.error}</span>
						</li>
					{/each}
				</ul>
			{/if}
		</div>
	{/if}
</div>

{#if loadError.length > 0}
	<EmptyState variant="error" title="The catalogue could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else}
	<!--
		The archived switch, above both panels because it governs both.

		It names the count it is hiding rather than only offering a switch. A default that hides
		things with no sign they exist is the "Show ended subscriptions" mistake §13 already made
		once: a plan an admin archived last year and has forgotten is indistinguishable from one
		that was never authored, and the first thing they do is create it again.
	-->
	{#if archivedCount > 0 || showArchived}
		<div class="controlpanel mb-4 flex flex-wrap items-center gap-3">
			<Toggle bind:checked={showArchived} />
			<span class="text-sm">
				Show archived
				{#if archivedCount > 0}
					<span class="text-default">
						({archivedCount}
						{archivedCount === 1 ? 'entry is' : 'entries are'}
						{showArchived ? 'shown' : 'hidden'}
					</span>
				{/if}
			</span>
		</div>
	{/if}

	<div class="space-y-4">
		<FamiliesPanel {families} {clickwraps} {productOptions} {loading} {showArchived} on:changed={fnLoad} />

		<ProductsPanel
			{products}
			{showArchived}
			currencies={$availableCurrencies}
			{families}
			{sellers}
			{clickwraps}
			{productOptions}
			{applications}
			providerTypes={providers.map(provider => provider.type)}
			currency={$activeCurrency}
			{loading}
			syncStatus={productSyncStatus}
			bind:selectedProductUUID
			on:changed={fnLoad}
		/>

		<CouponsPanel {coupons} {products} {families} currencies={$availableCurrencies} {loading} on:changed={fnLoad} />

		<ClickwrapsPanel {clickwraps} {products} {families} {loading} on:changed={fnLoad} />

		<!--
			Directly under the terms, because they are the same shape of thing authored by the same
			person and differ in one word: a term is required and an option is offered.
		-->
		<ProductOptionsPanel
			options={productOptions}
			{products}
			{families}
			currencies={$availableCurrencies}
			{loading}
			on:changed={fnLoad}
		/>
	</div>

	<!--
		Prices open over the catalogue rather than below it. The panel used to sit at the bottom of
		a long page, so choosing Prices on a product scrolled the thing you asked for out of sight
		and left no indication anything had happened.

		Keyed on the product so the panel remounts per product: it holds a draft price and a
		reprice preview, and carrying either across a switch would price the wrong plan.
	-->
	<Modal
		open={selectedProduct !== null}
		size="xl"
		outsideclose
		on:close={() => (selectedProductUUID = '')}
	>
		<!--
			The dialog opened with no titlebar at all, so the one thing an admin needed to read
			before typing an amount — which plan this is — was on the table behind the backdrop.
		-->
		<svelte:fragment slot="header">
			<ModalHeader
				title={`Prices for ${selectedProduct?.name ?? ''}`}
				faIcon="tag"
				subtitle={selectedProduct === null
					? ''
					: `${selectedProduct.familyName ?? 'Not in a family'} · ${selectedProduct.slug}`}
			/>
		</svelte:fragment>

		{#key selectedProductUUID}
			<PricesPanel
				product={selectedProduct}
				currencies={$availableCurrencies}
				embedded
				on:changed={fnLoad}
			/>
		{/key}
	</Modal>
{/if}
